'use client'

import { Link, usePathname, useRouter } from '@/shared/navigation'
import type { ReactNode } from 'react'

import { kitchenTerms } from '@/features/kitchen/kitchenTerms'

import { clearKitchenSession } from '@/shared/auth/sessionStorage'

import { getKitchenDevice } from '@/shared/auth/deviceStorage'

import { SyncStatusIndicator } from '@/shared/components/SyncStatusIndicator'
import { useClientHydrated } from '@/shared/hooks/useClientHydrated'

const nav = [
  { to: '/kitchen/home', label: kitchenTerms.modules.home, icon: 'home' },
  { to: '/kitchen/production', label: kitchenTerms.modules.production, icon: 'skillet' },
  { to: '/kitchen/dispatch', label: kitchenTerms.modules.dispatch, icon: 'local_shipping' },
  { to: '/kitchen/waste', label: kitchenTerms.modules.waste, icon: 'delete_sweep' },
  { to: '/kitchen/attendance', label: kitchenTerms.modules.attendance, icon: 'face_retouching_natural' },
] as const

export function KitchenShell({ children }: { children: ReactNode }) {
  const hydrated = useClientHydrated()
  const device = hydrated ? getKitchenDevice() : null
  const router = useRouter()
  const pathname = usePathname()

  function handleLogout() {
    clearKitchenSession()
    router.replace('/kitchen/login')
  }

  return (
    <div className="min-h-screen flex flex-col max-w-5xl mx-auto">
      <header className="sticky top-0 z-10 bg-surface-container-lowest border-b border-outline-variant px-4 h-14 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="material-symbols-outlined text-primary shrink-0">skillet</span>
          <div className="min-w-0">
            <p className="font-headline text-sm font-bold text-primary leading-tight">
              {kitchenTerms.appName}
            </p>
            <p className="text-caption text-on-surface-variant font-mono truncate max-w-[160px]">
              {device ? `${device.kitchenName} · ${device.deviceName}` : '—'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <SyncStatusIndicator />
          <button
            type="button"
            onClick={handleLogout}
            className="text-xs text-on-surface-variant hover:text-primary whitespace-nowrap"
          >
            Keluar
          </button>
        </div>
      </header>

      <main className="flex-1 p-4 pb-20">{children}</main>

      <nav className="fixed bottom-0 left-0 right-0 bg-surface-container-lowest border-t border-outline-variant safe-area-pb">
        <div className="max-w-md mx-auto flex justify-around px-1 py-1">
          {nav.map((item) => {
            const isActive = pathname === item.to || pathname.startsWith(`${item.to}/`)
            return (
              <Link
                key={item.to}
                href={item.to}
                className={`flex flex-col items-center gap-0.5 px-2 py-2 rounded-lg text-caption min-w-[56px] min-h-[44px] justify-center transition-colors ${
                  isActive ? 'text-primary font-bold bg-primary-container/30' : 'text-on-surface-variant'
                }`}
              >
                <span className="material-symbols-outlined">{item.icon}</span>
                {item.label}
              </Link>
            )
          })}
        </div>
      </nav>
    </div>
  )
}
